import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { stockAdjustmentPath } from '../../lib/slug';
import { catalogApi } from '../catalog/catalogApi';
import { inventoryApi } from '../inventory/inventoryApi';
import { ADJUSTMENT_KINDS, ADJUSTMENT_REASONS, adjustmentsApi } from './adjustmentsApi';
import '../requisitions/requisitions.css';
import '../transfers/transfers.css';

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function AdjustmentFormPage() {
  const navigate = useNavigate();
  const t = useT();
  const toast = useToast();
  const { activePropertyId, hasPermission } = useProperty();
  const canAdjust = hasPermission('inventory.adjust');

  const [locations, setLocations] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [locationId, setLocationId] = useState('');
  const [kind, setKind] = useState('WriteOff');
  const [adjustmentDate, setAdjustmentDate] = useState(todayIso());
  const [notes, setNotes] = useState('');

  const [stock, setStock] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [qty, setQty] = useState({}); // itemId -> string
  const [reasons, setReasons] = useState({}); // itemId -> reason value

  const isCorrection = kind === 'Correction';

  useEffect(() => {
    let active = true;
    Promise.all([
      catalogApi.listLocations(activePropertyId).catch(() => []),
      catalogApi.listUnits().catch(() => []),
    ]).then(([locs, us]) => {
      if (!active) return;
      setLocations(locs || []);
      setUnits(us || []);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [activePropertyId]);

  // Load the location's stock whenever it changes.
  useEffect(() => {
    if (!locationId) { setStock([]); setQty({}); return; }
    let active = true;
    setStockLoading(true);
    inventoryApi.stock({ locationIds: locationId, includeZero: true, pageSize: 500, sortBy: 'item' })
      .then((res) => { if (active) setStock(res?.items || []); })
      .catch(() => { if (active) setStock([]); })
      .finally(() => { if (active) setStockLoading(false); });
    setQty({});
    return () => { active = false; };
  }, [locationId]);

  const locationOptions = useMemo(
    () => [{ value: '', label: t('Select a location…') }, ...locations.map((l) => ({ value: l.id, label: l.name }))],
    [locations, t],
  );
  const kindOptions = useMemo(
    () => ADJUSTMENT_KINDS.map((k) => ({ value: k.value, label: t(k.label) })),
    [t],
  );
  const reasonOptions = useMemo(
    () => [{ value: '', label: t('Default reason') }, ...ADJUSTMENT_REASONS.map((r) => ({ value: r.value, label: t(r.label) }))],
    [t],
  );

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;

  const filteredStock = useMemo(() => {
    const term = itemSearch.trim().toLowerCase();
    if (!term) return stock;
    return stock.filter((s) =>
      (s.itemName || '').toLowerCase().includes(term) || (s.sku || '').toLowerCase().includes(term));
  }, [stock, itemSearch]);

  const setItemQty = (itemId, value) => setQty((prev) => ({ ...prev, [itemId]: value }));
  const setItemReason = (itemId, value) => setReasons((prev) => ({ ...prev, [itemId]: value }));

  const selectedLines = useMemo(() =>
    stock
      .map((s) => ({ stock: s, q: Number(qty[s.itemId]) }))
      .filter((x) => !Number.isNaN(x.q) && x.q !== 0 && (isCorrection || x.q > 0)),
    [stock, qty, isCorrection],
  );

  const totalValue = useMemo(() => {
    const cur = stock.find((s) => s.averageCost)?.averageCost?.currency || 'USD';
    const sum = selectedLines.reduce((a, x) => a + Math.abs(x.q) * Number(x.stock.averageCost?.amount || 0), 0);
    return `${cur} ${formatAmount(sum)}`;
  }, [selectedLines, stock]);

  const kindHelp = isCorrection
    ? t('Corrections are signed: a positive quantity adds stock, a negative quantity removes it.')
    : kind === 'Issue'
      ? t('Issues record operational consumption (kitchen, bar, housekeeping) — the quantity leaves stock.')
      : t('Write-offs remove wasted, spoiled, damaged or expired stock. Attach photo evidence on the draft before posting.');

  const submit = useCallback(async (post) => {
    if (!locationId) { toast.error(t('Choose a location')); return; }
    if (selectedLines.length === 0) { toast.error(t('Enter a quantity for at least one item')); return; }
    const overdrawn = selectedLines.find((x) => (isCorrection ? -x.q : x.q) > x.stock.quantityOnHand);
    if (overdrawn) { toast.error(t('Cannot remove more {item} than the {quantity} on hand', { item: overdrawn.stock.itemName, quantity: overdrawn.stock.quantityOnHand })); return; }
    setBusy(true);
    try {
      const adjustment = await adjustmentsApi.create({
        locationId,
        kind,
        adjustmentDate: adjustmentDate ? new Date(adjustmentDate).toISOString() : null,
        notes: notes.trim() || null,
        lines: selectedLines.map((x) => ({
          itemId: x.stock.itemId,
          quantity: x.q,
          reason: reasons[x.stock.itemId] || null,
          notes: null,
        })),
        post,
      });
      toast.success(post ? t('Adjustment {number} posted', { number: adjustment.number }) : t('Draft adjustment saved — attach photos, then post it'));
      navigate(stockAdjustmentPath(adjustment));
    } catch (err) {
      toast.error(t('Could not create adjustment'), { description: err?.message });
      setBusy(false);
    }
  }, [locationId, kind, adjustmentDate, notes, selectedLines, reasons, isCorrection, navigate, toast, t]);

  if (!canAdjust) {
    return (
      <PageContainer size="md">
        <EmptyState icon={<Icon.Lock size={28} />} title={t('Not allowed')} description={t("You don't have permission to adjust stock.")} />
      </PageContainer>
    );
  }

  return (
    <PageContainer size="lg">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/adjustments')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Adjustments')}
      </Button>

      <PageHeader
        title={t('New adjustment')}
        description={t('Write off waste, issue stock for consumption, or record a correction. Posting updates the stock ledger immediately.')}
        icon={<Icon.TrendingDown size={22} />}
      />

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : locations.length === 0 ? (
        <Banner tone="warning">{t('A stock location is needed before adjustments can be recorded. Add locations in Settings first.')}</Banner>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Card>
            <Card.Body>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
                <div className="ps-field">
                  <span className="ps-field__label">{t('Location')}</span>
                  <Select value={locationId} onChange={setLocationId} ariaLabel={t('Location')} options={locationOptions} searchable />
                </div>
                <div className="ps-field">
                  <span className="ps-field__label">{t('Kind')}</span>
                  <Select value={kind} onChange={setKind} ariaLabel={t('Kind')} options={kindOptions} />
                </div>
                <Input label={t('Adjustment date')} type="date" value={adjustmentDate} onChange={(e) => setAdjustmentDate(e.target.value)} />
              </div>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-3)' }}>{kindHelp}</div>
            </Card.Body>
          </Card>

          {!locationId ? (
            <Card><Card.Body><EmptyState icon={<Icon.TrendingDown size={26} />} title={t('Choose a location')} description={t('Pick the location whose stock is being adjusted.')} /></Card.Body></Card>
          ) : (
            <Card>
              <Card.Header>
                <Card.Title>{t('Items to adjust')}</Card.Title>
                <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                  {isCorrection
                    ? t('Enter signed quantities: positive adds stock, negative removes it.')
                    : t("Enter the quantity leaving stock for each item. You can't remove more than what's on hand.")}
                </div>
              </Card.Header>
              <Card.Body>
                {stockLoading ? (
                  <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-10)' }}><Spinner size={22} /></div>
                ) : stock.length === 0 ? (
                  <EmptyState icon={<Icon.Boxes size={26} />} title={t('No stock records at this location')} description={t('Items appear here once they have a stock record at the location.')} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    <Input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder={t('Search items…')} leadingIcon={<Icon.Search size={15} />} />
                    <div className="trf-picker">
                      {filteredStock.map((s) => {
                        const q = qty[s.itemId] ?? '';
                        const selected = q !== '' && Number(q) !== 0;
                        const uom = uomCode(s.uomId) ? ` ${uomCode(s.uomId)}` : '';
                        return (
                          <div className={`trf-row ${selected ? 'is-selected' : ''}`} key={s.id}>
                            <div className="trf-row__item">
                              <div className="trf-row__name">{s.itemName || s.itemId}</div>
                              {s.sku && <div className="trf-row__sub">{s.sku}</div>}
                            </div>
                            <div className="trf-row__onhand">{t('{quantity} on hand', { quantity: `${s.quantityOnHand}${uom}` })}</div>
                            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                              <div className="trf-row__qty">
                                <Input
                                  type="number"
                                  min={isCorrection ? undefined : '0'}
                                  step="any"
                                  value={q}
                                  placeholder="0"
                                  onChange={(e) => setItemQty(s.itemId, e.target.value)}
                                />
                              </div>
                              {selected && (
                                <div style={{ minWidth: 170 }}>
                                  <Select
                                    value={reasons[s.itemId] || ''}
                                    onChange={(v) => setItemReason(s.itemId, v)}
                                    ariaLabel={t('Reason')}
                                    options={reasonOptions}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {filteredStock.length === 0 && (
                        <div style={{ padding: 'var(--space-4)' }} className="pp-cell-muted">{t('No items match “{search}”.', { search: itemSearch })}</div>
                      )}
                    </div>

                    <Textarea label={t('Notes')} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('Optional — what happened (e.g. freezer failure, breakage during service)')} />

                    <div className="trf-summary">
                      <div className="trf-route">
                        <Badge tone="neutral">{locations.find((l) => l.id === locationId)?.name}</Badge>
                      </div>
                      <div>
                        <span className="pp-cell-muted">{selectedLines.length === 1 ? t('{count} item', { count: selectedLines.length }) : t('{count} items', { count: selectedLines.length })} · </span>
                        <strong>{totalValue}</strong>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                      <Button variant="secondary" loading={busy} disabled={selectedLines.length === 0} leadingIcon={<Icon.Camera size={16} />} onClick={() => submit(false)}>
                        {t('Save draft (attach photos)')}
                      </Button>
                      <Button variant="primary" loading={busy} disabled={selectedLines.length === 0} leadingIcon={<Icon.Check size={16} />} onClick={() => submit(true)}>
                        {t('Post adjustment')}
                      </Button>
                      <Button variant="ghost" onClick={() => navigate('/adjustments')} disabled={busy}>{t('Cancel')}</Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </div>
      )}
    </PageContainer>
  );
}
